var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Mesh, MeshBasicMaterial, PlaneBufferGeometry, Vector3 } from 'three';
import { TextHorizontalAlignment, TextSizingMode, TextVerticalAlignment, } from 'shared/interfaces/planogram';
import { SphereItemType } from 'shared/interfaces/planogram';
import planogramConfig, { PLANOGRAM_CONFIG } from 'shared/config/PlanogramConfig';
import BaseObject3D from 'shared/object3d/BaseObject3D';
import { Text } from 'troika-three-text';
import { resolvePaletteColor } from 'shared/utils/colors';
import PlanogramBox from '../utils/PlanogramBox';
import { getVisibleText, textMeshRenderSize } from '../utils/TextUtils';
import { assertDefined, assertTrue } from '../utils/debug';
function awaitSync(text) {
    const needsSync = text._needsSync;
    assertDefined(needsSync, 'Troika text needsSync implementation changed');
    if (!needsSync)
        return Promise.resolve();
    return new Promise(resolve => {
        text.sync(resolve);
    });
}
function alignPosition(objectBox, horizontal, vertical, textHeight) {
    const center = objectBox.center();
    const start = objectBox.start();
    const end = objectBox.end();
    let x;
    let y;
    switch (horizontal) {
        case TextHorizontalAlignment.CENTER:
            x = center.x;
            break;
        case TextHorizontalAlignment.RIGHT:
            x = end.x;
            break;
        case TextHorizontalAlignment.LEFT:
        default:
            x = start.x;
    }
    switch (vertical) {
        case TextVerticalAlignment.MIDDLE:
            y = center.y + textHeight / 2;
            break;
        case TextVerticalAlignment.BOTTOM:
            y = start.y + textHeight;
            break;
        case TextVerticalAlignment.TOP:
        default:
            y = end.y;
    }
    return [x, y];
}
function align(mesh, config, textHeight) {
    const box = new PlanogramBox().fromCenterAndSize(config.position, {
        x: config.scale[0],
        y: config.scale[1],
    });
    const [x, y] = alignPosition(box, config.alignment, config.verticalAlignment, textHeight);
    mesh.position.set((x - config.position.x) / config.scale[0], (y - config.position.y) / config.scale[1], 0);
}
function applyBaseParameters(mesh, textAreaConfig, palette) {
    mesh.frustumCulled = false;
    const resolvedColor = resolvePaletteColor(textAreaConfig.color, palette);
    mesh.color = resolvedColor;
    mesh.outlineColor = resolvedColor;
    mesh.overflowWrap = textAreaConfig.overflowWrap;
    mesh.font = textAreaConfig.fontFamily.file_url;
    if (textAreaConfig.autoFit)
        mesh.fontSize = textAreaConfig.effectiveFontSize * PLANOGRAM_CONFIG.fontUnitSize;
    else
        mesh.fontSize = textAreaConfig.fontSize * PLANOGRAM_CONFIG.fontUnitSize;
    mesh.textAlign = textAreaConfig.alignment;
    mesh.anchorX = textAreaConfig.alignment;
    mesh.lineHeight = textAreaConfig.lineHeight / textAreaConfig.fontSize;
    mesh.letterSpacing = textAreaConfig.letterSpacing / textAreaConfig.fontSize;
    mesh.scale.copy(new Vector3(1, 1, 1).divide(new Vector3(...textAreaConfig.scale)));
    mesh.maxWidth = maxWidth(textAreaConfig);
}
function maxWidth(config) {
    if (config.resizingMode === TextSizingMode.FIXED_HEIGHT)
        return planogramConfig.MAX_OBJECT_SIZE.x;
    else
        return config.scale[0];
}
const backgroundGeometry = new PlaneBufferGeometry();
const transparentMaterial = new MeshBasicMaterial({
    depthTest: false,
    transparent: true,
    opacity: 0,
});
class TextAreaObject3D extends BaseObject3D {
    constructor() {
        super();
        this.overflowing = false;
        this.itemType = SphereItemType.TextArea;
        this.measureMesh = new Text();
        this.measureMesh.name = 'MeasureMesh';
        this.add(this.measureMesh);
        this.clippedMesh = new Text();
        this.clippedMesh.name = 'ClippedMesh';
        this.add(this.clippedMesh);
        this.updateVisibility(false);
        const clickableBackgroundMesh = new Mesh(backgroundGeometry, transparentMaterial);
        clickableBackgroundMesh.name = 'TextAreaBackgroundMesh';
        this.add(clickableBackgroundMesh);
    }
    isOverflowing() {
        return this.overflowing;
    }
    updateMesh(mesh, config, palette) {
        return __awaiter(this, void 0, void 0, function* () {
            assertTrue(mesh === this.clippedMesh || mesh === this.measureMesh, 'Unexpected mesh');
            const isEdit = mesh === this.measureMesh;
            applyBaseParameters(mesh, config, palette);
            mesh.material.opacity = isEdit === this.editMode ? config.alpha : 0;
            yield awaitSync(mesh);
            const clippedHeight = textMeshRenderSize(mesh).y;
            align(mesh, config, clippedHeight);
        });
    }
    configure(config, palette) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.measureMesh.text = config.text;
            yield this.updateMesh(this.measureMesh, config, palette);
            [this.clippedMesh.text, this.overflowing] = (_a = getVisibleText(this.measureMesh, config)) !== null && _a !== void 0 ? _a : [
                '',
                false,
            ];
            yield this.updateMesh(this.clippedMesh, config, palette);
        });
    }
    updateVisibility(edit) {
        if (edit !== this.editMode) {
            const opacity = Math.max(this.measureMesh.material.opacity, this.clippedMesh.material.opacity);
            this.clippedMesh.material.opacity = edit ? 0 : opacity;
            this.measureMesh.material.opacity = edit ? opacity : 0;
            this.editMode = edit;
        }
    }
    dispose() {
        this.measureMesh.dispose();
        this.clippedMesh.dispose();
    }
}
export default TextAreaObject3D;
